import { isImageField } from '@/domain/image/isImageField';
import { isTextboxField } from '@/domain/textbox/isTextboxField';
import { ImageLoaderManager } from '@/infrastructure/engine2d/assets/ImageLoaderManager';
import { FABRIC_CONTROL_COLOR, FONT_SIZE_MULTIPLIER } from '@/infrastructure/engine2d/engine2dConstants';
import { ImageField } from '@/domain/image/ImageField';
import { TextboxField } from '@/domain/textbox/TextboxField';
import { FabricImage, Textbox } from 'fabric';
import type { AField } from '@/domain/field/AField';
import type { FabricScene } from '@/infrastructure/engine2d/FabricScene';
import type { IFabricImageCustom } from '@/infrastructure/engine2d/types/IFabricImageCustom';
import type { ITextboxCustom } from '@/infrastructure/engine2d/types/ITextboxCustom';
import type { IGroup } from '@/infrastructure/engine2d/types/IGroup';

// Preload the rotation icon so the first cursor-drag on a control
// doesn't stall on an image fetch.
ImageLoaderManager.loadImage('/images/rotate-alt.svg');

// Single dispatch point Fabric-side. Called once per entity from
// FabricSceneWriteService.addView (boot loops, live add). Returns the
// created fabric element for image / textbox (so the caller can move
// it into the locked-foreground group when needed); returns undefined
// for cut fields — the cut path lives in its own layer group, has no
// per-entity fabric object, and is intentionally fire-and-forget
// (boot's Promise.all doesn't await SVG load, preserving pre-refacto
// timing).
//
// `editable` gates fabric's own editable + selectable flags; when the
// caller doesn't project one, we default to `!field.isLocked`.
async function addField(
    field: AField,
    params: {
        addToCanvas?: boolean;
        editable?: boolean;
        fabricWorldInstance: FabricScene;
    },
): Promise<IFabricImageCustom | ITextboxCustom | undefined> {
    const { addToCanvas = true, fabricWorldInstance } = params;
    const canvas = fabricWorldInstance?.fabricCanvas;

    // Idempotence guard — skip fields already present on the canvas.
    if (canvas?.getObjects().some((object) => (object as { id?: string }).id === field.id)) {
        return undefined;
    }

    const editable = params.editable ?? !field.isLocked;

    const fElem = await _buildFieldView(field, { addToCanvas, editable, fabricWorldInstance });
    if (fElem) {
        _setRotationIconOnControl(fElem);
    }
    return fElem;
}

// The one place a field kind picks its fabric builder. Deliberately two branches rather than a
// kind-keyed registry: with two kinds and no third planned, a registry cannot type the
// correlation between a key and its builder's parameter without a cast -- the very thing the
// field guards just removed from eleven call sites. What was worth removing was the identical
// tail both branches carried, which is now in addField.
async function _buildFieldView(
    field: AField,
    options: { addToCanvas: boolean; editable: boolean; fabricWorldInstance: FabricScene },
): Promise<IFabricImageCustom | ITextboxCustom | undefined> {
    if (field instanceof TextboxField) {
        return _addTextboxField(field, options);
    }
    if (field instanceof ImageField) {
        return _addImageField(field, options);
    }

    console.error(`addField: no fabric builder for field kind ${JSON.stringify(field.kind)}`);
    return undefined;
}

function _addTextboxField(
    textboxField: TextboxField,
    params: {
        addToCanvas: boolean;
        editable: boolean;
        fabricWorldInstance: FabricScene;
    },
): Promise<ITextboxCustom> {
    const { addToCanvas = true, editable, fabricWorldInstance } = params;
    const { fabricCanvas } = fabricWorldInstance;
    const fTextbox = _textboxFieldToFText(textboxField, { fabricWorldInstance, editable });
    fTextbox.setControlsVisibility({
        tl: false,
        tr: false,
        bl: false,
        br: false,
        mt: false,
        mb: false,
    });
    _centerToCanvas(fTextbox, { field: textboxField, fabricWorldInstance });

    fTextbox.set('dirty', true);

    if (addToCanvas) {
        fabricCanvas.add(fTextbox);
    }
    return Promise.resolve(fTextbox);
}

async function _addImageField(
    imageField: ImageField,
    params: {
        addToCanvas: boolean;
        editable: boolean;
        fabricWorldInstance: FabricScene;
        smoothImage?: boolean;
    },
): Promise<IFabricImageCustom | undefined> {
    const { addToCanvas = true, editable, fabricWorldInstance, smoothImage = true } = params;
    const { fabricCanvas } = fabricWorldInstance;
    if (!imageField.url) {
        return Promise.resolve(undefined);
    }
    let fImage: FabricImage | undefined;
    const loadImage = ImageLoaderManager.loadImage(imageField.url);
    return loadImage.then((img) => {
        if (img instanceof Error) {
            return undefined;
        }
        const { behavior } = imageField;
        fImage = new FabricImage(img, {
            angle: imageField.rotationDeg,
            cornerColor: FABRIC_CONTROL_COLOR,
            cornerStrokeColor: FABRIC_CONTROL_COLOR,
            editable,
            frameSize: {
                height: fabricCanvas.height,
                width: fabricCanvas.width,
            },
            hasControls: !behavior?.hideControls,
            id: imageField.id,
            imageSmoothing: smoothImage,
            left: imageField.position.x,
            lockMovementY: Boolean(behavior?.lockY),
            opacity: imageField.opacity ?? 1,
            perPixelTargetFind: !behavior?.hideControls,
            scaleX: imageField.scale.x,
            scaleY: imageField.scale.y,
            selectable: editable,
            top: imageField.position.y,
        });
        if (behavior?.constrainToFrameX) {
            (fImage as FabricImage & { constrainToFrameX?: boolean }).constrainToFrameX = true;
        }
        if (behavior?.alwaysOnTop) {
            (fImage as FabricImage & { alwaysOnTop?: boolean }).alwaysOnTop = true;
        }
        if (addToCanvas) {
            // Idempotence guard — re-check after the async URL load.
            const alreadyPresent = fabricCanvas.getObjects().some((object) => (object as { id?: string }).id === imageField.id);
            if (alreadyPresent) {
                fImage.dispose();
                fImage = undefined;
                return undefined;
            }
            const mainImgGroup = (fabricCanvas.getObjects('group') as IGroup[]).find((group) => group?.id === 'mainImgGroupName');
            if (mainImgGroup) {
                mainImgGroup.add(fImage);
                mainImgGroup.set('dirty', true);
            } else {
                fabricCanvas.add(fImage);
            }
            _centerToCanvas(fImage as IFabricImageCustom, { field: imageField, fabricWorldInstance });
            if (behavior?.fitToFrameHeight) {
                const scaleFactor = fabricWorldInstance.imageRenderHeight / fImage.height;
                fImage.set('top', 0);
                fImage.set('scaleX', scaleFactor);
                fImage.set('scaleY', scaleFactor);
                fImage.setCoords();
                fImage.set('dirty', true);
            }
        }
        return fImage as IFabricImageCustom | undefined;
    });
}

function _textboxFieldToFText(
    field: TextboxField,
    params: {
        editable: boolean;
        fabricWorldInstance: FabricScene;
    },
): ITextboxCustom {
    const { fabricCanvas } = params.fabricWorldInstance;
    const { height, width } = fabricCanvas;
    const charSpacing = (field.characterSpacing ?? 0) * FONT_SIZE_MULTIPLIER * 4;
    const lineSpacingOffset = 16 / (FONT_SIZE_MULTIPLIER * 4);
    const textboxWidth = field.size?.width ?? 0;
    const textboxHeight = field.size?.height ?? 0;
    const fText = new Textbox(field.text || field.placeholder || '', {
        angle: field.rotationDeg,
        centeredRotation: true,
        charSpacing,
        cornerColor: FABRIC_CONTROL_COLOR,
        cornerStrokeColor: FABRIC_CONTROL_COLOR,
        editable: params.editable,
        fill: field.color,
        fontFamily: field.fontFamily,
        fontSize: field.fontSize * FONT_SIZE_MULTIPLIER,
        fontStyle: field.isItalic ? 'italic' : 'normal',
        fontWeight: field.isBold ? 'bold' : 'normal',
        frameSize: { height, width },
        id: field.id,
        left: field.position.x,
        lineHeight: (field.lineSpacing === 0 ? 0.01 : (field.lineSpacing ?? 1) / (FONT_SIZE_MULTIPLIER * 4)) + lineSpacingOffset,
        name: 'textName',
        objectCaching: false,
        scaleX: field.scale.x,
        scaleY: field.scale.y,
        selectable: params.editable,
        splitByGrapheme: false,
        styles: field.styles || {},
        textAlign: field.textAlign ?? 'left',
        top: field.position.y,
        underline: field.isUnderlined,
        ...(textboxWidth + textboxHeight !== 0 ? { width: textboxWidth, height: textboxHeight } : {}),
    });

    return fText as ITextboxCustom;
}

function _centerToCanvas(
    fObject: IFabricImageCustom | ITextboxCustom,
    params: {
        fabricWorldInstance: FabricScene;
        field: AField;
    },
) {
    // Per axis, not on the sum: a legitimate (-5, 5) sums to zero and would be recentred.
    if (fObject.left !== 0 || fObject.top !== 0) {
        return;
    }
    const { fabricCanvas, imageRenderHeight, imageRenderWidth } = params.fabricWorldInstance;
    const left = (imageRenderWidth - (fObject.width || 0)) / 2;
    const top = (imageRenderHeight - (fObject.height || 0)) / 2;
    fObject.set('left', left);
    fObject.set('top', top);
    fObject.setCoords();
    fObject.set('dirty', true);
    // The entity is the same reference held by the Pinia store, so
    // mutating in place is enough — no direct store call from an
    // infrastructure helper.
    const entity = params.field;
    entity.position = { x: fObject.left, y: fObject.top };
    // Both kinds carry `size` and both take the same value here; the branch is only there
    // because AField does not declare it. Narrowing rather than casting at least keeps it honest.
    if (isImageField(entity) || isTextboxField(entity)) {
        entity.size = { width: fObject.width, height: fObject.height };
    }
    fabricCanvas.renderAll();
}

function _setRotationIconOnControl(fObject: IFabricImageCustom | ITextboxCustom) {
    fObject.controls.mtr.render = function (ctx, left, top) {
        const { x, y } = this;
        ImageLoaderManager.loadImage('/images/rotate-alt.svg').then((rotationIcon) => {
            if (rotationIcon instanceof Error) {
                console.error('Error loading image:', rotationIcon);
                return;
            }
            const { height, width } = rotationIcon;
            ctx.save();
            ctx.translate(left - width / 2, top - height / 2);
            ctx.drawImage(rotationIcon, x, y);
            ctx.restore();
        });
    };
}

export { addField };
